import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetSchema } from '@/hooks/api/useGetSchema';
import { useEscapeHotkey } from '@/hooks/hotkeys';
import { FiltersSection } from '@/sections/insights/queryBuilder/components/QueryVisualizer/sections/FiltersSection';
import { LabelSection } from '@/sections/insights/queryBuilder/components/QueryVisualizer/sections/LabelSection';
import { MetadataSection } from '@/sections/insights/queryBuilder/components/QueryVisualizer/sections/MetadataSection';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { FlatGraphNode } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { NodeLabel } from '@/types';
import { NODE_LABEL_VALUES } from '@/types';
import { pluralize } from '@/utils/pluralize.util';
import { isValidNodeLabel } from '@/utils/validation.util';

interface NodeDetailsModalProps {
  nodeId: string;
  nodeDetails: FlatGraphNode['data'];
  isExpanded: boolean;
  editable: boolean;
  onClose: () => void;
}

export const NodeDetailsModal = ({
  nodeId,
  nodeDetails,
  isExpanded,
  editable,
  onClose,
}: NodeDetailsModalProps) => {
  const { dispatch } = useQueryData();

  // Form state (local copy of nodeDetails for editing)
  const [editedDetails, setEditedDetails] =
    useState<FlatGraphNode['data']>(nodeDetails);

  // Track the baseline value to prevent auto-save on reset
  const baselineDetailsRef = useRef<FlatGraphNode['data']>(nodeDetails);

  // Reset form when nodeDetails changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEditedDetails(nodeDetails);
    baselineDetailsRef.current = nodeDetails;
  }, [nodeDetails]);

  // Fetch schema once
  const {
    data: schema,
    isLoading: schemaLoading,
    error: schemaError,
  } = useGetSchema();

  // Get field suggestions from all node labels
  const suggestedFields = useMemo(() => {
    const labels = nodeDetails.labels || [];
    const fieldSet = new Set<string>();

    // Iterate over all labels and collect fields from matching entities
    labels.forEach(label => {
      // Convert label to entity type (pluralize lowercase)
      const entityType = pluralize(2, label.toLowerCase());

      const entity = getQueryEntity(entityType);
      if (entity) {
        const fields = entity.getFields(schema);
        fields.forEach(field => fieldSet.add(field.key));
      }
    });

    return Array.from(fieldSet).sort();
  }, [nodeDetails.labels, schema]);

  // Validation stub for auto-save
  const validateDetails = (_details: FlatGraphNode['data']): boolean => {
    // TODO: Implement real validation in Phase 2
    // For now, always return true to allow all edits
    return true;
  };

  // Auto-save to query context only when user makes actual changes
  useEffect(() => {
    // Skip if not editable
    if (!editable) {
      return;
    }

    // Skip auto-save if editedDetails matches the baseline (no user edits)
    if (
      JSON.stringify(editedDetails) ===
      JSON.stringify(baselineDetailsRef.current)
    ) {
      return;
    }

    if (validateDetails(editedDetails)) {
      dispatch({ type: 'UPDATE_NODE', nodeId, data: editedDetails });
    }
  }, [editedDetails, editable, dispatch, nodeId]);

  // Close on Escape key (only while the modal is expanded)
  useEscapeHotkey(onClose, { enabled: isExpanded });

  if (!isExpanded) return null;

  const handleLabelsChange = (labels: FlatGraphNode['data']['labels']) => {
    setEditedDetails({ ...editedDetails, labels });
  };

  const handleFiltersChange = (filters: FlatGraphNode['data']['filters']) => {
    setEditedDetails({ ...editedDetails, filters });
  };

  const handleMetadataChange = (metadata: {
    alias?: string;
    sorted?: boolean;
    selfEdges?: 'forbid' | 'allow' | 'require';
  }) => {
    setEditedDetails({
      ...editedDetails,
      alias: metadata.alias,
      sorted: metadata.sorted,
      self_edges: metadata.selfEdges,
    });
  };

  return (
    <div
      data-testid={`node-details-modal-${nodeId}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`modal-title-${nodeId}`}
      className="bg-chariot-background-primary border-chariot-stroke pointer-events-auto flex h-full flex-col rounded border shadow-lg"
    >
      {/* Header - Fixed */}
      <div className="border-chariot-stroke flex flex-shrink-0 items-center justify-between border-b p-2">
        <h3
          id={`modal-title-${nodeId}`}
          className="text-chariot-text-primary text-xs font-semibold"
        >
          Node Details
        </h3>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-chariot-text-secondary hover:text-chariot-text-primary"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        <LabelSection<NodeLabel>
          labels={editedDetails.labels || []}
          editable={editable}
          onChange={handleLabelsChange}
          validateLabel={isValidNodeLabel}
          suggestedLabels={[...NODE_LABEL_VALUES]}
        />

        {schemaLoading && (
          <div className="text-chariot-text-secondary p-2 text-xs">
            Loading field suggestions...
          </div>
        )}

        {schemaError && (
          <div className="p-2 text-xs text-red-500">
            Failed to load field suggestions
          </div>
        )}

        <FiltersSection
          filters={editedDetails.filters || []}
          editable={editable}
          onChange={handleFiltersChange}
          suggestedFields={suggestedFields}
        />

        <MetadataSection
          alias={editedDetails.alias}
          sorted={editedDetails.sorted}
          selfEdges={editedDetails.self_edges}
          editable={editable}
          onChange={handleMetadataChange}
        />
      </div>
    </div>
  );
};
